import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Camera } from 'lucide-react'
import { useProjectStore } from '../../stores/projectStore'

interface CreateSceneDialogProps {
  open: boolean
  onClose: () => void
  projectId: string
  initialImageId?: string | null
}

const CreateSceneDialog: React.FC<CreateSceneDialogProps> = ({ open, onClose, projectId, initialImageId }) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const { createScene, loading } = useProjectStore()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim()) return

    try {
      await createScene({
        title: title.trim(),
        description: description.trim(),
        projectId,
        orderIndex: 0 // Will be set correctly by the store
      }, initialImageId || undefined) // Pass initialImageId if present
      handleClose()
    } catch (error) {
      console.error('Failed to create scene:', error)
    }
  }

  const handleClose = () => {
    setTitle('')
    setDescription('')
    onClose()
  }

  if (!open) return null

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center z-[60] p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={handleClose}
      >
        <motion.div
          className="glass-card w-full max-w-md border border-white/20"
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center">
                <Camera className="w-5 h-5 text-primary-300" />
              </div>
              <h2 className="text-xl font-semibold text-white">
                {initialImageId ? 'Create Scene from Image' : 'Create New Scene'}
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="glass-button p-2 hover:bg-white/20"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {initialImageId && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 text-sm text-blue-200">
                The selected image will be moved to this new scene.
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Scene Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter scene title..."
                className="glass-input w-full"
                maxLength={100}
                required
                autoFocus
              />
              <div className="text-xs text-white/50 mt-1">
                {title.length}/100 characters
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this scene (optional)..."
                className="glass-input w-full h-24 resize-none"
                maxLength={500}
              />
              <div className="text-xs text-white/50 mt-1">
                {description.length}/500 characters
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 glass-button text-white/90 hover:bg-white/10 py-2.5"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 glass-button bg-primary-500/30 hover:bg-primary-500/40 text-white font-semibold py-2.5 disabled:opacity-50"
                disabled={loading || !title.trim()}
              >
                {loading ? 'Creating...' : 'Create Scene'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default CreateSceneDialog 